import {
  Card,
  FlatButton,
  Icon,
  IconsUnion,
  Typography,
} from '@globalmed/atlas-ui';
import { memo, useMemo } from 'react';
import type { FocusEvent, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { formBuilderElements } from '../constants';
import styles from './FormBuilderCardWrapper.module.css';
import { Line } from '@/components/Line';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import type { EFormBuilderFieldType } from '@/types';

type TProps = {
  variant: EFormBuilderFieldType;
  active?: boolean;
  error?: boolean;
  onActiveChange: (active: boolean) => void;
  onDownMove: () => void;
  onUpMove: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
};

export const FormBuilderCardWrapper =
  /**
   * Wrapper component for form builder field cards with controls for moving, duplicating and removing fields
   * @param {TProps} props - Component props
   * @param {EFormBuilderFieldType} props.variant - Type of the form field
   * @param {boolean} [props.active=false] - Whether the card is currently active/selected
   * @param {boolean} [props.error=false] - Whether the field has validation errors
   * @param {Function} props.onActiveChange - Callback when active state changes
   * @param {Function} props.onDownMove - Callback to move field down
   * @param {Function} props.onUpMove - Callback to move field up
   * @param {Function} props.onDuplicate - Callback to duplicate field
   * @param {Function} props.onRemove - Callback to remove field
   * @param {React.ReactNode} props.children - Child elements to render inside the card
   * @returns {JSX.Element} Form builder card wrapper component
   */
  memo(
    ({
      variant,
      active = false,
      children,
      error = false,
      onActiveChange,
      onDownMove,
      onUpMove,
      onDuplicate,
      onRemove,
    }: PropsWithChildren<TProps>) => {
      const { t } = useTranslation();
      const titleValues = formBuilderElements[variant];

      const settingsButtons = useMemo(
        () =>
          [
            {
              icon: 'ChevronDown',
              onClick: onDownMove,
              label: t('form-builder.move-field-down'),
            },
            {
              icon: 'ChevronUp',
              onClick: onUpMove,
              label: t('form-builder.move-field-up'),
            },
            {
              icon: 'Copy',
              onClick: onDuplicate,
              label: t('form-builder.duplicate-field'),
            },
            {
              icon: 'Trash',
              onClick: onRemove,
              label: t('form-builder.remove-field'),
            },
          ] satisfies Array<{
            icon: IconsUnion;
            onClick: () => void;
            label: string;
          }>,
        [onDownMove, onDuplicate, onRemove, onUpMove, t],
      );

      const ref = useOutsideClick<HTMLDivElement>(
        () => active && onActiveChange(false),
      );

      const handleFocus = () => {
        if (!active) onActiveChange(true);
      };

      const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (active && !event.currentTarget.contains(event.relatedTarget)) {
          onActiveChange(false);
        }
      };

      return (
        <Card
          ref={ref}
          variant="outlined"
          onClick={() => onActiveChange(true)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          padding="16px 24px"
          sx={{
            // TODO: need fix in atlas UI
            outline: active ? '2px solid var(--colors-primary600)' : undefined,
          }}
          className={styles.container}
        >
          <div className={styles.titleWrapper}>
            <div className={styles.titleInfo}>
              <div className={styles.titleIcon}>
                <Icon
                  color="var(--colors-primary600)"
                  name={titleValues.icon}
                />
              </div>
              <Typography fontWeight={600}>{t(titleValues.value)}</Typography>
            </div>
            <div className={styles.settings}>
              {settingsButtons.map((button) => (
                <FlatButton
                  key={button.icon}
                  onClick={button.onClick}
                  aria-label={button.label}
                >
                  <Icon
                    name={button.icon}
                    color="var(--colors-gray950)"
                    size="20px"
                    sx={{ padding: '2px' }}
                    decorative
                  />
                </FlatButton>
              ))}
              {error && (
                <Icon
                  name="AlertCircle"
                  color="var(--colors-error700)"
                  size="20px"
                  sx={{ padding: '2px' }}
                  role="alert"
                  aria-label={t('form-builder.field-has-errors')}
                />
              )}
            </div>
          </div>
          <Line />
          {children}
        </Card>
      );
    },
  );

FormBuilderCardWrapper.displayName = 'FormBuilderCardWrapper';
